import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { selectStoreConfig, selectCategories } from "../redux/Slice/siteSlice";
import { selectProducts } from "../redux/Slice/productSlice";
import Category from "./Category";
import ScrollRow from "./ScrollRow";
import ProductCard from "./ProductCard";
import { ProductSkeletons } from "./Loader";
import { getFeaturedProducts, getHomeRows, getOfferProducts } from "../services/products";
import { OFFERS } from "../utils/ownezzy";
import OfferDeals from "./OfferDeals";
import useStoreType, { categoryIcon } from "../utils/storeTypes";

const SPOTLIGHT_TINTS = ["mint", "peach", "lilac"];

// Home page: banner slider, featured plants and one row per category
export default function Home({ go, query, filtered, add, onSelect, cart, updateQty }) {
    const config = useSelector(selectStoreConfig);
    const cats = useSelector(selectCategories);
    const products = useSelector(selectProducts);
    const type = useStoreType();

    // Featured section: chosen by the owner in the website editor (newest 12 until they choose).
    // If it cannot load, the first 12 products of the catalogue stand in.
    const [featured, setFeatured] = useState(null);
    useEffect(() => {
        let live = true;
        getFeaturedProducts()
            .then((items) => live && setFeatured(items))
            .catch(() => live && setFeatured(false));
        return () => { live = false; };
    }, []);
    const featuredItems = featured || (featured === false ? products.slice(0, 12) : []);

    // One row per category with its own count and first products, and the store's total, from the server (the
    // catalogue in the store state is only the first 100 products). If it cannot load, those 100 stand in.
    const [home, setHome] = useState(null);
    useEffect(() => {
        let live = true;
        getHomeRows().then((data) => live && setHome(data)).catch(() => live && setHome(false));
        return () => { live = false; };
    }, []);
    const totalProducts = home ? home.total : products.length;
    const rows = home ? home.rows : home === false ? cats.map((cat) => ({ category: cat, products: products.filter((p) => p.cat === cat) })) : [];

    // Offers row: the products the owner put in Offers (website editor)
    const [offers, setOffers] = useState([]);
    useEffect(() => {
        if (!config.hasOffers) return setOffers([]);
        let live = true;
        getOfferProducts().then((items) => live && setOffers(items)).catch(() => live && setOffers([]));
        return () => { live = false; };
    }, [config.hasOffers]);

    // The owner's hero banners from the website editor: { title, description, image } -> { title, sub, img }; phones
    // get the banner's tall mobile image when it has one
    const banners = useMemo(() => {
        const phone = typeof window !== "undefined" && window.matchMedia("(max-width: 640px)").matches;
        const list = (config.heroBanners || []).map((banner) => ({ title: banner.title, sub: banner.description, img: (phone && banner.mobileImage) || banner.image }));
        if (list.length > 0) return list;
        return [
            { title: "Bring a little jungle home", sub: "Fresh greens, cheerful colors and plants for every corner.", img: "https://cdn.ownezzy.com/library/pexels-1084199.webp" },
            { title: "Make every room feel alive", sub: "Low-light favorites for desks, shelves and cozy corners.", img: "https://cdn.ownezzy.com/library/pexels-3126442.webp" },
            { title: "Tropical energy, everyday", sub: "Statement palms and foliage made for beautiful spaces.", img: "https://cdn.ownezzy.com/library/pexels-9731992.webp" },
            { title: "Breathe fresh with green companions", sub: "Air-purifying and easy-care plants delivered with love.", img: "https://cdn.ownezzy.com/library/pexels-3489128.webp" }
        ];
    }, [config.heroBanners]);

    // Keep the slider state local so Home works independently of its parent.
    const [slide, setSlide] = useState(0);
    const [isHeroPaused, setIsHeroPaused] = useState(false);
    const bannerCount = banners.length;

    useEffect(() => {
        if (isHeroPaused || bannerCount < 2) return undefined;
        const timer = window.setInterval(() => {
            setSlide((current) => (current + 1) % bannerCount);
        }, 5000);
        return () => window.clearInterval(timer);
    }, [isHeroPaused, bannerCount]);

    // Avoid a crash if the banner data changes while the page is mounted.
    const activeSlide = banners.length ? ((slide % bannerCount) + bannerCount) % bannerCount : 0;
    const activeBanner = banners[activeSlide];
    const heroImage = (activeBanner && activeBanner.img) || (products[0] && products[0].img) || "";
    return (
        <>
            <section
                className="hero"
                aria-roledescription="carousel"
                aria-label={type.featuredTitle}
                onMouseEnter={() => setIsHeroPaused(true)}
                onMouseLeave={() => setIsHeroPaused(false)}
                onFocusCapture={() => setIsHeroPaused(true)}
                onBlurCapture={(event) => {
                    if (!event.currentTarget.contains(event.relatedTarget)) setIsHeroPaused(false);
                }}
            >
                <div className="heroGlow g1" />
                <div className="heroGlow g2" />
                <div className="heroCopy">
                    <span className="eyebrow"><span className="eyebrowDot" /> {type.heroEyebrow}</span>
                    <h1>{activeBanner?.title || config.name}</h1>
                    <p>{activeBanner?.sub || config.about}</p>
                    <div className="heroBtns">
                        <button className="primaryCta" onClick={() => go("Products", "All")}>
                            {type.heroCta} <span>→</span>
                        </button>
                        <button className="ghost" onClick={() => go("Products", "All")}>
                            Explore collection
                        </button>
                    </div>
                    <div className="miniStats">
                        <span>{type.icon} {totalProducts} favorites</span>
                        {type.heroStats.map((stat) => <span key={stat}>{stat}</span>)}
                    </div>
                </div>
                <div
                    className="heroPhoto"
                    style={{ backgroundImage: heroImage ? `url(${heroImage})` : undefined }}
                >
                    <div className="photoBadge">
                        {type.badgeTop}
                        <br />
                        <b>{type.badgeBottom}</b>
                    </div>
                    <div className="heroFloatingChip" aria-hidden="true">
                        <span className="chipSparkle">✨</span>
                        <div>
                            <b>100% Handpicked Quality</b>
                            <small>Carefully selected daily</small>
                        </div>
                    </div>
                </div>
                {bannerCount > 1 && (
                    <>
                        <button
                            className="arr a1"
                            onClick={() =>
                                setSlide((current) => (current - 1 + bannerCount) % bannerCount)
                            }
                        >
                            ‹
                        </button>
                        <button
                            className="arr a2"
                            onClick={() => setSlide((current) => (current + 1) % bannerCount)}
                        >
                            ›
                        </button>
                        <div className="dots">
                            {banners.map((_, i) => (
                                <button
                                    key={i}
                                    onClick={() => setSlide(i)}
                                    className={i === activeSlide ? "dot on" : "dot"}
                                />
                            ))}
                        </div>
                    </>
                )}
            </section>
            {/* Trust points and highlight cards: edited in the website editor (Content tab) */}
            {!config.highlightsOff && <div className="trust">
                {(config.highlights || []).filter((card) => !card.hidden).map((card, index) => (
                    <div className={`trustCard trustTint-${index % 4}`} key={index}>
                        <div className="trustIconWrap"><i>{card.icon}</i></div>
                        <div>
                            <b>{card.title}</b>
                            <span>{card.text}</span>
                        </div>
                    </div>
                ))}
            </div>}
            <section className="colorCards">
                {(config.spotlights || []).map((card, index) => (
                    <div className={`colorCard ${SPOTLIGHT_TINTS[index % SPOTLIGHT_TINTS.length]}`} key={index}>
                        <span>{card.icon}</span>
                        <div>
                            <b>{card.title}</b>
                            <small>{card.text}</small>
                        </div>
                    </div>
                ))}
            </section>
            <ScrollRow className="pills" wrapClassName="pillsRow" label="Shop by category" autoScroll>
                {offers.length > 0 && <button className="pill-offers" onClick={() => go("Products", OFFERS)}>🏷️ {config.offersTitle}</button>}
                {cats.map((c) => (
                    <button key={c} onClick={() => go("Products", c)}>
                        {categoryIcon(type, c)} {c}
                    </button>
                ))}
            </ScrollRow>
            {(featured === null || featuredItems.length > 0) && <section className="section featuredSection">
                <div className="head">
                    <div>
                        <span className="sectionEyebrow">Handpicked Specials</span>
                        <h2>{type.featuredTitle}</h2>
                        <p>{type.featuredText}</p>
                    </div>
                </div>
                {/* A plain grid (no sideways scrolling), then a link to the full list */}
                <div className="productGridItems featuredGrid">
                    {featured === null ? (
                        <ProductSkeletons count={8} />
                    ) : (
                        featuredItems.map((product) => (
                            <ProductCard key={product.id} p={product} add={add} onSelect={onSelect} cart={cart} updateQty={updateQty} />
                        ))
                    )}
                </div>
                {totalProducts > 0 && (
                    <div className="featuredMore">
                        <button type="button" className="featuredMoreBtn" onClick={() => go("Products", "All")}>
                            View all {totalProducts} products <span className="featuredMoreArrow" aria-hidden="true">→</span>
                        </button>
                    </div>
                )}
            </section>}
            {!query && offers.length > 0 && (
                <OfferDeals title={config.offersTitle} items={offers} add={add} onSelect={onSelect} cart={cart} updateQty={updateQty} viewAll={() => go("Products", OFFERS)} />
            )}
            {query ? (
                <Category
                    cat="Search results"
                    items={filtered}
                    add={add}
                    onSelect={onSelect}
                    cart={cart}
                    updateQty={updateQty}
                    viewAll={() => go("Products", "All")}
                />
            ) : (
                rows.map((row) => (
                    <Category
                        key={row.category}
                        cat={row.category}
                        items={row.products}
                        total={row.total}
                        add={add}
                        onSelect={onSelect}
                        cart={cart}
                        updateQty={updateQty}
                        viewAll={() => go("Products", row.category)}
                    />
                ))
            )}
        </>
    );
}
